import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businessTypes = [
  "Office",
  "Gym",
  "Retail Shop",
  "School",
  "Restaurant",
  "Other",
];

const services = [
  "Regular Contract Cleaning",
  "Deep Cleaning",
  "Window Cleaning",
  "Not sure — please advise",
];

export default function QuoteForm({ defaultService }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    business_type: "",
    service: defaultService || "",
    location: "",
    message: "",
    website: "", // honeypot — never display, never edit programmatically
  });

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-form-success"
        className="rounded-3xl bg-white border border-slate-200 shadow-soft p-8 sm:p-10 text-center"
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-[#4CAF50]/15 inline-flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-[#388E3C]" />
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-5">
          Quote request received
        </h3>
        <p className="text-slate-600 mt-3">
          Thanks {form.name.split(" ")[0] || "there"} — a member of our Cork
          team will be in touch within one working day.
        </p>
        <Button
          data-testid="quote-form-reset"
          onClick={() => {
            setDone(false);
            setForm({
              name: "",
              email: "",
              phone: "",
              business_name: "",
              business_type: "",
              service: defaultService || "",
              location: "",
              message: "",
              website: "",
            });
          }}
          variant="outline"
          className="mt-6 rounded-full"
        >
          Send another request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className="relative overflow-hidden rounded-3xl bg-white border border-slate-200 shadow-soft-lg p-6 sm:p-8 lg:p-10"
    >
      <div className="mb-6 sm:mb-8">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Get Your Free Quote
        </h2>
        <p className="mt-2 text-slate-600 text-sm sm:text-base">
          No obligation. We'll respond quickly — usually within one working
          day.
        </p>
        <p
          data-testid="form-response-time"
          className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#388E3C] bg-[#4CAF50]/10 rounded-full px-3 py-1"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#4CAF50]" />
          Average response time: under 24 hours
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Honeypot — hidden from humans, often filled by bots */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
        >
          <label htmlFor="qf-website">Website (leave blank)</label>
          <input
            id="qf-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
          />
        </div>
        <div>
          <Label htmlFor="qf-name" className="text-slate-800 font-medium">
            Your name <span className="text-[#4CAF50]">*</span>
          </Label>
          <Input
            id="qf-name"
            data-testid="quote-input-name"
            value={form.name}
            onChange={update("name")}
            placeholder="Jane Murphy"
            className="mt-2 h-12 rounded-xl"
            required
          />
        </div>
        <div>
          <Label htmlFor="qf-business" className="text-slate-800 font-medium">
            Business name
          </Label>
          <Input
            id="qf-business"
            data-testid="quote-input-business"
            value={form.business_name}
            onChange={update("business_name")}
            placeholder="Murphy & Co."
            className="mt-2 h-12 rounded-xl"
          />
        </div>
        <div>
          <Label htmlFor="qf-email" className="text-slate-800 font-medium">
            Email <span className="text-[#4CAF50]">*</span>
          </Label>
          <Input
            id="qf-email"
            data-testid="quote-input-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="jane@business.ie"
            className="mt-2 h-12 rounded-xl"
            required
          />
        </div>
        <div>
          <Label htmlFor="qf-phone" className="text-slate-800 font-medium">
            Phone <span className="text-[#4CAF50]">*</span>
          </Label>
          <Input
            id="qf-phone"
            data-testid="quote-input-phone"
            value={form.phone}
            onChange={update("phone")}
            placeholder="085 123 4567"
            className="mt-2 h-12 rounded-xl"
            required
          />
        </div>

        <div>
          <Label className="text-slate-800 font-medium">Business type</Label>
          <Select
            value={form.business_type}
            onValueChange={(v) => setForm((s) => ({ ...s, business_type: v }))}
          >
            <SelectTrigger
              data-testid="quote-select-business-type"
              className="mt-2 h-12 rounded-xl"
            >
              <SelectValue placeholder="Select a type" />
            </SelectTrigger>
            <SelectContent>
              {businessTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-slate-800 font-medium">Service needed</Label>
          <Select
            value={form.service}
            onValueChange={(v) => setForm((s) => ({ ...s, service: v }))}
          >
            <SelectTrigger
              data-testid="quote-select-service"
              className="mt-2 h-12 rounded-xl"
            >
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent>
              {services.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="qf-location" className="text-slate-800 font-medium">
            Location in Cork
          </Label>
          <Input
            id="qf-location"
            data-testid="quote-input-location"
            value={form.location}
            onChange={update("location")}
            placeholder="e.g. Cork City Centre, Douglas, Ballincollig"
            className="mt-2 h-12 rounded-xl"
          />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="qf-message" className="text-slate-800 font-medium">
            Tell us about your space
          </Label>
          <Textarea
            id="qf-message"
            data-testid="quote-input-message"
            value={form.message}
            onChange={update("message")}
            rows={4}
            placeholder="Size of premises, frequency, preferred days/times…"
            className="mt-2 rounded-xl"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="quote-submit-btn"
        className="mt-7 w-full sm:w-auto h-12 px-8 rounded-full bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold btn-primary-glow"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            Request Your Quote in 60 Seconds
          </>
        )}
      </Button>

      <p
        data-testid="form-trust-boost"
        className="mt-4 text-sm font-medium text-slate-700"
      >
        No obligation. No pressure. Just a quick quote.
      </p>

      <p className="mt-2 text-xs text-slate-500">
        By submitting, you agree we may contact you about your enquiry. No
        spam.
      </p>
    </form>
  );
}
